import { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { useLiveData } from "@/context/LiveDataContext";
import { fmtTime } from "@/lib/format";
import { toast } from "sonner";
import { X, Eye } from "lucide-react";
import { PostflightOrchestrationPanel } from "@/components/PostflightOrchestrationPanel";
import { getOrchestrationOutcomeBadge } from "@/lib/orchestration";
import { px4SourceLabel } from "@/lib/px4Source";

const TYPE_COLOR = {
  command_ack: "#22C55E", command_queued: "#3B82F6", mission_created: "#3B82F6",
  drone_ws_connected: "#22C55E", drone_ws_disconnected: "#525252",
  ws_error: "#EF4444", api_error: "#EF4444", orchestration_ended: "#3B82F6",
  px4_statustext: "#8B5CF6", px4_event: "#8B5CF6", px4_observation: "#06B6D4",
};

// A.5.4H.4C — sévérité PX4 réellement reçue uniquement (jamais fabriquée).
const SEV_COLOR = {
  EMERGENCY: "#EF4444", ALERT: "#EF4444", CRITICAL: "#EF4444", ERROR: "#EF4444",
  WARNING: "#F59E0B", NOTICE: "#3B82F6", INFO: "#A3A3A3", DEBUG: "#525252",
};

// A.5.4H.4C.2 — libellé de source (PX4 vs observation SkyCore) : helper pur partagé/testable (voir @/lib/px4Source).

// Normalise un événement PX4 canonique en ligne de journal (aucune cause inventée).
function px4Row(ev) {
  const kind = ev.kind;
  let type = "px4_observation";
  let message = "";
  if (kind === "statustext") {
    type = "px4_statustext";
    message = (ev.text || "") + (ev.text_incomplete ? "  [texte incomplet]" : "");
  } else if (kind === "event") {
    // EVENT non décodé : on n'invente JAMAIS de libellé, on expose l'ID brut.
    type = "px4_event";
    message = `EVENT #${ev.event_id ?? "?"} (seq ${ev.sequence ?? "?"})`;
  } else if (ev.transition === "event_sequence") {
    message = `Séquence d'événements PX4 : ${ev.sequence_status}${ev.dropped ? ` (${ev.dropped} perdu(s))` : ""}`;
  } else {
    message = `${ev.transition} : ${String(ev.from)} → ${String(ev.to)}`;
  }
  return {
    event_uid: ev.event_uid, when: ev.ts_utc, drone_id: ev.drone_id,
    type, status: ev.severity_label || "—", message, source: px4SourceLabel(ev.source),
    severity_label: ev.severity_label || null,
  };
}

export default function Logs() {
  const { events } = useLiveData();
  const [persisted, setPersisted] = useState([]);
  const [px4Rest, setPx4Rest] = useState([]);   // A.5.4H.4C — événements PX4 via Gateway (REST)
  // A.5.4E — modale post-vol session-aware : résolue par drone_id + session_id (JAMAIS last_orchestration).
  const [session, setSession] = useState(null);
  const [orch, setOrch] = useState(null);
  const [unavailable, setUnavailable] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/logs", { params: { limit: 200 } }).then(({ data }) => setPersisted(data.events || [])).catch(() => {});
    // Graceful : si l'endpoint PX4 Gateway n'est pas disponible, on garde une liste vide.
    api.get("/px4-events", { params: { limit: 200 } })
      .then(({ data }) => setPx4Rest(data?.available && Array.isArray(data.events) ? data.events : []))
      .catch(() => setPx4Rest([]));
  }, []);

  const openFlight = async (drone_id, session_id) => {
    setSession({ drone_id, session_id }); setOrch(null); setSummary(null); setUnavailable(null);
    try {
      const { data } = await api.get(`/drones/${drone_id}/orchestrations/${session_id}`);
      if (data.available) setOrch(data.orchestration);
      else setUnavailable("Historique d'orchestration non disponible");
    } catch (e) { setUnavailable("Historique d'orchestration non disponible"); }
  };

  const genSummary = async () => {
    if (!session) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/drones/${session.drone_id}/orchestrations/${session.session_id}/postflight-summary`);
      if (data.available) setSummary(data.summary);
      else { setUnavailable("Historique d'orchestration non disponible"); toast.info("Session introuvable"); }
    } catch (e) { toast.error(apiErr(e, "Synthèse post-vol indisponible")); }
    finally { setLoading(false); }
  };

  const closeModal = () => { setSession(null); setOrch(null); setSummary(null); setUnavailable(null); };

  // A.5.4H.4C — Séparation des événements PX4 (dédupliqués par event_uid) du reste du flux.
  const livePx4 = events.filter((e) => e && e.event_uid);
  const liveOther = events.filter((e) => !(e && e.event_uid));
  const seenUid = new Set(livePx4.map((e) => e.event_uid));
  const px4Merged = [
    ...livePx4.map((e) => px4Row(e)),
    ...px4Rest.filter((e) => !seenUid.has(e.event_uid)).map((e) => px4Row(e)),
  ];

  const liveKeys = new Set(liveOther.map((e) => e.command_id + e.type + e.message));
  const merged = [
    ...liveOther.map((e) => ({ ...e, when: e.ts })),
    ...persisted.filter((p) => !liveKeys.has(p.command_id + p.type + p.message)).map((p) => ({ ...p, when: p.created_at })),
    ...px4Merged,
  ].sort((a, b) => new Date(b.when) - new Date(a.when));

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="font-chivo text-3xl font-black tracking-tighter uppercase">Journal événements</h1>
        <p className="font-mono text-xs text-[#A3A3A3] mt-1">Flux temps réel des commandes et de la télémétrie</p>
      </div>

      <div className="border border-white/10 rounded-sm overflow-hidden">
        <div className="grid grid-cols-[90px_100px_130px_110px_1fr_90px] gap-2 px-3 py-2 bg-[#141414] border-b border-white/10 font-mono text-[9px] uppercase tracking-[0.15em] text-[#525252]">
          <span>Heure</span><span>Drone</span><span>Type</span><span>Statut</span><span>Message</span><span>Source</span>
        </div>
        <div className="max-h-[70vh] overflow-y-auto" data-testid="logs-table">
          {merged.length === 0 && <div className="px-3 py-8 text-center font-mono text-xs text-[#525252]">Aucun événement pour l'instant</div>}
          {merged.map((e, i) => {
            // Cliquable UNIQUEMENT si fin d'orchestration AVEC drone_id + session_id (aucun fallback, aucune supposition).
            const canView = e.type === "orchestration_ended" && !!e.drone_id && !!e.session_id;
            // A.5.4F — badge d'issue VISUEL depuis e.outcome porté par l'événement (aucun fetch supplémentaire, aucun parsing de message).
            const badge = e.type === "orchestration_ended" ? getOrchestrationOutcomeBadge(e.outcome) : null;
            return (
              <div key={(e._id || e.id || e.event_uid || i) + "-" + i} data-testid={e.event_uid ? `logs-px4-row` : undefined} className="grid grid-cols-[90px_100px_130px_110px_1fr_90px] gap-2 px-3 py-2 border-b border-white/5 font-mono text-[11px] hover:bg-white/5">
                <span className="text-[#A3A3A3]">{fmtTime(e.when)}</span>
                <span>{e.drone_id || "—"}</span>
                <span style={{ color: TYPE_COLOR[e.type] || "#A3A3A3" }}>{e.type}{e.command ? `:${e.command}` : ""}</span>
                <span className="text-[#A3A3A3] min-w-0">
                  {badge ? (
                    <span data-testid={`logs-outcome-${e.session_id || "na"}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider" style={{ color: badge.color }}>
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: badge.color }} />
                      <span className="truncate">{badge.label}</span>
                    </span>
                  ) : (
                    <span style={e.severity_label ? { color: SEV_COLOR[e.severity_label] || "#A3A3A3" } : undefined}>{e.status || "—"}</span>
                  )}
                </span>
                <span className="text-[#A3A3A3] flex items-center gap-2 min-w-0" title={e.message}>
                  <span className="truncate">{e.message || "—"}</span>
                  {canView && (
                    <button data-testid={`logs-postflight-${e.session_id}`} onClick={() => openFlight(e.drone_id, e.session_id)}
                      className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#3B82F6] hover:text-[#60A5FA] border border-[#3B82F6]/30 rounded-sm px-1.5 py-0.5">
                      <Eye size={11} /> Voir le vol
                    </button>
                  )}
                </span>
                <span className="text-[#525252]">{e.source || "—"}</span>
              </div>
            );
          })}
        </div>
      </div>

      {session && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="logs-postflight-modal">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-md w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="font-mono text-[11px] uppercase tracking-wider text-white/70" data-testid="logs-postflight-session">
                Historique — session {session.session_id}
              </div>
              <button data-testid="logs-postflight-close" onClick={closeModal} className="text-white/50 hover:text-white"><X size={16} /></button>
            </div>
            <div className="p-4">
              {orch ? (
                <PostflightOrchestrationPanel
                  orchestration={orch}
                  title={`Vol ${session.drone_id}`}
                  subtitle="(historique — pas l'état live)"
                  summary={summary}
                  loading={loading}
                  onGenerate={genSummary}
                  generateLabel="Générer la synthèse IA"
                  containerTestid="logs-orchestration-panel"
                  buttonTestid="logs-generate-summary"
                  resultTestid="logs-postflight-result"
                />
              ) : (
                <p data-testid="logs-postflight-unavailable" className="text-[11px] text-white/40">{unavailable || "Chargement…"}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
